/* --- tabs ----------------------------------------------------------------- *
 *
 * An underline on the active tab, drawn on the same rule that closes the tab
 * strip. One line does both jobs: separates the tabs from their content and
 * marks the selection.
 */

.tabs {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.tabList {
  display: flex;
  align-items: stretch;
  gap: var(--space-6);
  border-bottom: var(--border-width-thin) solid var(--color-border-default);
  overflow-x: auto;
  scrollbar-width: none;
}

.tabList::-webkit-scrollbar {
  display: none;
}

.tab {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-1);
  /* The indicator is a transparent border at rest, so selecting a tab changes
   * a colour and never a size: a tab strip that reflows on selection makes the
   * neighbouring tab move under the pointer. */
  border-bottom: 3px solid transparent;
  margin-bottom: calc(var(--border-width-thin) * -1);
  font-size: var(--text-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.tab:hover {
  color: var(--color-text-primary);
}

.tab[data-state="active"] {
  color: var(--color-accent-fg);
  border-bottom-color: var(--color-accent-emphasis);
}

.tab:focus-visible {
  outline-offset: calc(var(--focus-ring-offset) * -1);
}

.tabCount {
  min-width: 20px;
  padding: 0 var(--space-3);
  text-align: center;
  background: var(--color-surface-sunken);
  border-radius: var(--radius-full);
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
  color: var(--color-text-secondary);
}

.tab[data-state="active"] .tabCount {
  background: var(--color-accent-bg);
  color: var(--color-accent-fg);
}

.tabPanel {
  padding-top: var(--section-gap);
}

.tabPanel:focus-visible {
  outline-offset: 0;
}

/* --- breadcrumb ------------------------------------------------------------- */

.breadcrumb {
  min-width: 0;
}

.breadcrumbList {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

.breadcrumbItem {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
  font-size: var(--text-sm);
  line-height: var(--text-sm-line);
}

.breadcrumbLink {
  color: var(--color-accent-fg);
  font-weight: var(--font-weight-medium);
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.breadcrumbLink:hover {
  color: var(--color-text-primary);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.breadcrumbCurrent {
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.breadcrumbSeparator {
  flex: none;
  color: var(--color-text-tertiary);
}

/* --- avatar ------------------------------------------------------------------ */

.avatar {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: var(--color-accent-bg);
  border: var(--border-width-thin) solid
    color-mix(in oklab, var(--color-accent-emphasis) 30%, transparent);
  /* A square with a small radius, not a circle. A circle reads as social; a
   * rounded square reads as a record, which is what a user is here. */
  border-radius: var(--radius-md);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.02em;
  color: var(--color-accent-fg);
  user-select: none;
}

.avatar-sm {
  width: 24px;
  height: 24px;
  font-size: 10px;
}

.avatar-md {
  width: 28px;
  height: 28px;
}

.avatar-lg {
  width: 48px;
  height: 48px;
  font-size: var(--text-lg);
  border-radius: var(--radius-lg);
}

.avatarImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* --- keyboard hint ------------------------------------------------------------ */

.kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 var(--space-2);
  background: var(--color-surface);
  border: var(--border-width-thin) solid var(--color-border-default);
  border-radius: var(--radius-sm);
  font-family: var(--font-family-ui);
  font-size: var(--text-xs);
  line-height: 1;
  color: var(--color-text-tertiary);
}
